import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, Menu, X } from 'lucide-react';
import './InfoPage.css';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <FileText className="badge-icon" />
              <span>TERMS OF SERVICE</span>
            </div>
            <h1 className="hero-title">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="hero-subtitle">
              Last updated: October 13, 2025
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <h2>Agreement to Terms</h2>
          <p>
            By accessing or using the HomeQuest Tech platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use our Service.
          </p>

          <h2>Description of Service</h2>
          <p>
            HomeQuest Tech provides an AI-powered construction management platform that includes:
          </p>
          <ul>
            <li>CAD file processing with AI room detection and selection placement</li>
            <li>AI site analysis with GIS county system integration</li>
            <li>AI communications including call handling, email composition, and message replies</li>
            <li>Smart vendor bidding with CAD file sharing and bid comparison</li>
            <li>Smart phases and budget tracking with AI invoice analysis</li>
            <li>AI photo intelligence for automated site documentation</li>
          </ul>

          <h2>Beta Program</h2>
          <p>
            Our platform is currently in beta testing with limited founding member spots. By participating in the beta program:
          </p>
          <ul>
            <li>You receive a 30% discount on your subscription for life</li>
            <li>You get a 2-week free trial to test all features</li>
            <li>You acknowledge the platform may contain bugs or incomplete features</li>
            <li>You agree to provide feedback to help improve the platform</li>
            <li>Beta pricing and terms are locked in for the lifetime of your account</li>
          </ul>

          <h2>Account Registration</h2>
          <p>
            To use our Service, you must:
          </p>
          <ul>
            <li>Be at least 18 years old</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </ul>

          <h2>Acceptable Use</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Upload viruses, malware, or other malicious code</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Reverse engineer, decompile, or disassemble our software</li>
            <li>Use automated systems to access the Service without permission</li>
            <li>Share your account credentials with unauthorized users</li>
            <li>Resell or redistribute our Service without authorization</li>
            <li>Remove or modify any proprietary notices or labels</li>
          </ul>

          <h2>Your Content</h2>
          <p>
            You retain ownership of all CAD files, project data, and content you upload ("Your Content"). By using our Service, you grant us a license to:
          </p>
          <ul>
            <li>Store, process, and display Your Content to provide the Service</li>
            <li>Use AI to analyze CAD files, invoices, photos, and other data</li>
            <li>Share Your Content with vendors when you initiate bid requests</li>
            <li>Backup Your Content to ensure data integrity and availability</li>
          </ul>
          <p>
            You represent that you have the right to upload Your Content and that it does not violate any third-party rights.
          </p>

          <h2>Payment Terms</h2>
          <h3>Subscription Plans</h3>
          <ul>
            <li>Beta founding members receive 30% off for life</li>
            <li>Subscriptions are billed monthly or annually based on your selection</li>
            <li>All payments are processed securely through Stripe</li>
            <li>Prices are in USD and subject to applicable taxes</li>
          </ul>

          <h3>Free Trial</h3>
          <ul>
            <li>2-week free trial with full access to all features</li>
            <li>Credit card required for trial signup</li>
            <li>Trial automatically expires unless you choose to subscribe</li>
          </ul>

          <h3>Refunds</h3>
          <ul>
            <li>Refunds are issued at our discretion on a case-by-case basis</li>
            <li>Contact support@homequesttech.com for refund requests</li>
            <li>Unused portions of annual subscriptions may be prorated</li>
          </ul>

          <h2>Cancellation</h2>
          <p>
            You may cancel your subscription at any time through your account settings or by contacting support. Upon cancellation:
          </p>
          <ul>
            <li>You retain access until the end of your current billing period</li>
            <li>No refunds for partial months or unused time</li>
            <li>Your data will be retained for 90 days, then permanently deleted</li>
            <li>You can export your data before the 90-day deletion period</li>
          </ul>

          <h2>GIS and Third-Party Data</h2>
          <p>
            Our Service integrates with GIS county systems and other third-party data sources. We are not responsible for:
          </p>
          <ul>
            <li>Accuracy or completeness of third-party data</li>
            <li>Availability or uptime of county GIS systems</li>
            <li>Compliance with local zoning or building codes based on our analysis</li>
          </ul>
          <p>
            You are responsible for verifying all information with appropriate authorities before making construction decisions.
          </p>

          <h2>AI Services</h2>
          <p>
            Our AI features are designed to assist with construction management but may not be 100% accurate. You acknowledge that:
          </p>
          <ul>
            <li>AI room detection and placement suggestions should be reviewed by humans</li>
            <li>AI invoice analysis may contain errors and should be verified</li>
            <li>AI communications should be monitored and may require human oversight</li>
            <li>AI-generated notes from photos are suggestions, not professional assessments</li>
          </ul>

          <h2>Intellectual Property</h2>
          <p>
            The Service, including all software, designs, text, graphics, and trademarks, is owned by HomeQuest Tech and protected by intellectual property laws. You may not copy, modify, or create derivative works without our written permission.
          </p>

          <h2>Warranty Disclaimer</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>

          <h2>Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, HOMEQUEST TECH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION.
          </p>

          <h2>Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless HomeQuest Tech from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
          </p>

          <h2>Changes to Terms</h2>
          <p>
            We may modify these Terms at any time. We will notify you of material changes via email or platform notification. Continued use of the Service after changes constitutes acceptance of the modified Terms.
          </p>

          <h2>Termination</h2>
          <p>
            We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent or abusive behavior. Upon termination, your right to use the Service immediately ceases.
          </p>

          <h2>Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Georgia, USA, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Georgia.
          </p>

          <h2>Entire Agreement</h2>
          <p>
            These Terms, along with our Privacy Policy, constitute the entire agreement between you and HomeQuest Tech regarding the Service.
          </p>

          <h2>Contact Us</h2>
          <p>
            If you have questions about these Terms of Service, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> support@homequesttech.com</li>
            <li><strong>Address:</strong> HomeQuest Tech, Atlanta, GA</li>
          </ul>

          <button onClick={() => navigate('/contact')} className="cta-button">
            Contact Support
          </button>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/privacy')}>Privacy</a>
              <a onClick={() => navigate('/security')}>Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
